import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { fetchUserInfo } from '../services/api';
import { trackLoginSuccess } from '../services/analytics';

interface UserInfo {
  uid?: number;
  name: string;
  domain: string;
  addr?: string;
  level?: string;
  plan_status?: string;
  plan?: {
    name: string;
    mxPlan?: any;
    endTime?: number;
  };
  aliasCount?: number;
  fwd?: number;
  rpy?: number;
  pin?: string;
  send_pin?: string;
  curMonth?: string;
  month_total?: number;
  domains?: string[];
  limits: {
    aliasLimits: number;
    dailylimits?: number;
    monthlyLimits: number;
  };
}

interface UserContextType {
  userInfo: UserInfo | null;
  loading: boolean;
  error: Error | null;
  refetchUserInfo: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async ({ showLoading = true } = {}) => {
    try {
      if (showLoading) setLoading(true);
      const response = await fetchUserInfo();
      if (response.code === 0) {
        setUserInfo(response.result);
        trackLoginSuccess(response.result.uid);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch user info'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData({ showLoading: true });
  }, [fetchData]);

  const refetchUserInfo = useCallback(async () => {
    await fetchData({ showLoading: false });
  }, [fetchData]);

  const value = useMemo(
    () => ({ userInfo, loading, error, refetchUserInfo }),
    [userInfo, loading, error, refetchUserInfo]
  );

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
